'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Configure } from 'react-instantsearch';

import { InstantSearchProvider, getDefaultFilters } from '@commons/components/Search';
import DropdownFilter from '@commons/components/Search/DropdownFilter';
import SearchInput from '@commons/components/Search/Input';
import SearchSortBy from '@commons/components/Search/SortBy';
import SearchStats from '@commons/components/Search/Stats';
import Flex from '@commons/containers/Flex';
import { ALGOLIA_INDEX_REPLICA_DESC } from '@commons/utils/constants';

import { insightsTypeFilters } from '@/components/Search';
import { ATTRIBUTES_TO_RETRIEVE } from '@/components/Search/Hit';
import SearchLayout from '@/components/Search/Layout';

import TopicFilter from './TopicFilter';
import styles from './index.module.scss';

/**
 * For the insights, the default index is set to the replica index ordered by date descending.
 *
 * This ensure that the default sort by value is 'latest' instead of 'relevance'.
 */

const DEFAULT_INDEX = ALGOLIA_INDEX_REPLICA_DESC;

/**
 * Insights search variant of the AlgoliaSearchBlock component.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/configure/react/
 */

const InsightsSearch = () => {
  const t = useTranslations('search.filters');
  const locale = useLocale();

  const defaultFilters = getDefaultFilters(locale);
  const filters = `${defaultFilters} AND (${insightsTypeFilters})`;

  const renderedTopBar = (
    <Flex gap="xs" wrap="wrap" width={12} align="center" justify={{ xs: 'start', lg: 'end' }}>
      <SearchSortBy
        className={styles.sortBy}
        rootClassName={styles.sortByTrigger}
        selectClassName={styles.sortBySelect}
      />
      <SearchStats />
    </Flex>
  );

  const renderedSidebar = (
    <>
      <SearchInput />
      <div>
        <DropdownFilter label={t('format')} attribute="format" defaultOpen />
      </div>
      <TopicFilter />
    </>
  );

  return (
    <InstantSearchProvider
      indexName={DEFAULT_INDEX}
      enableEmptySearch
      initialSearchUrl="/api/search/initial/insights"
    >
      <Configure filters={filters} hitsPerPage={12} attributesToRetrieve={ATTRIBUTES_TO_RETRIEVE} />
      <SearchLayout hasSidebar topChildren={renderedTopBar} sidebarChildren={renderedSidebar} />
    </InstantSearchProvider>
  );
};

export default InsightsSearch;
